/**
 * Utility functions for generating and managing stable track identifiers.
 * 
 * Stable track IDs persist across participant reconnections, eliminating the need
 * for complex track replacement logic and simplifying mode switching between
 * live (WebRTC) and recorded (HLS) video sources.
 */

/**
 * Generates a stable track identifier that persists across reconnections.
 * 
 * The stable ID is composed of:
 * - Participant identity (not SID, as SID can change)
 * - Track name (e.g., "video", "screen_share")
 * - Track source (e.g., "camera", "screen_share")
 * 
 * @param {Object} track - Track object with participant and track info
 * @param {Object} [track.hls] - HLS track data
 * @param {Object} [track.hls.participant] - HLS participant data
 * @param {string} [track.hls.participant.identity] - HLS participant identity
 * @param {Object} [track.webrtcRef] - WebRTC track reference
 * @param {Object} [track.webrtcRef.participant] - WebRTC participant
 * @param {string} [track.webrtcRef.participant.identity] - WebRTC participant identity
 * @param {string} [track.participantIdentity] - Direct participant identity
 * @param {string} track.name - Track name
 * @param {string} track.source - Track source
 * @param {string} track.id - Original track ID (fallback)
 * @returns {string} Stable identifier (e.g., "student-123_video_camera")
 * 
 * @example
 * const track = {
 *   id: "TR_abc123",
 *   participantIdentity: "student-123",
 *   name: "video",
 *   source: "camera"
 * };
 * const stableId = generateStableTrackId(track);
 * // Returns: "student-123_video_camera"
 */
export const generateStableTrackId = (track) => {
  // Use participant identity (not SID, as SID can change)
  const participantIdentity = getParticipantIdentity(track);
  
  // Use track name (e.g., "video", "screen_share")
  const trackName = track.name;
  
  // Use track source (e.g., "camera", "screen_share")
  const trackSource = track.source;
  
  if (!participantIdentity || !trackName || !trackSource) {
    console.error('[trackIdentifiers] Cannot generate stable track ID - missing required fields', {
      participantIdentity,
      trackName,
      trackSource,
      track
    });
    // Fallback to original ID
    return track.id;
  }
  
  return `${participantIdentity}_${trackName}_${trackSource}`;
};

/**
 * Extracts participant identity from various track formats.
 * 
 * Handles both HLS tracks (from recorded sessions) and WebRTC tracks (from live sessions).
 * 
 * @param {Object} track - Track object
 * @returns {string|null} Participant identity or null if not found
 * 
 * @example
 * // HLS track
 * const hlsTrack = { hls: { participant: { identity: "student-123" } } };
 * getParticipantIdentity(hlsTrack); // "student-123"
 * 
 * // WebRTC track
 * const webrtcTrack = { webrtcRef: { participant: { identity: "student-123" } } };
 * getParticipantIdentity(webrtcTrack); // "student-123"
 */
export const getParticipantIdentity = (track) => {
  return track.hls?.participant?.identity || 
         track.webrtcRef?.participant?.identity ||
         track.participantIdentity ||
         null;
};

/**
 * Checks if two tracks represent the same logical stream.
 * 
 * Two tracks are considered the same if they have the same stable ID,
 * even if their original track IDs differ (due to reconnection).
 * 
 * @param {Object} track1 - First track
 * @param {Object} track2 - Second track
 * @returns {boolean} True if tracks represent the same logical stream
 * 
 * @example
 * const oldTrack = { id: "TR_abc", participantIdentity: "student-123", name: "video", source: "camera" };
 * const newTrack = { id: "TR_xyz", participantIdentity: "student-123", name: "video", source: "camera" };
 * isSameLogicalTrack(oldTrack, newTrack); // true (same participant, name, source)
 */
export const isSameLogicalTrack = (track1, track2) => {
  return generateStableTrackId(track1) === generateStableTrackId(track2);
};
